import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { setMarches as setMarchesCache, setPricesBatch, setSparklinesBatch } from '../../redux/marketSlice';
import { getMarches, getDataForCurrentMinutes, getPricesBatch, getSparklineBatch, funcAchat, funcVente, getPortefeuilleData } from '../../Api/authApi.js.js';
import StockChart from '../../components/chartgraphique/StockChart/index.js';
import './marches.css';

const CATEGORY_META = {
  actions:     { label: 'Actions',            accent: '#7AB8E0', bg: '#1f2d4a', desc: "Devenir actionnaire d'une entreprise." },
  obligations: { label: 'Obligations',        accent: '#9B7BE0', bg: '#241f3f', desc: "Prêter à un État ou une entreprise." },
  matieres:    { label: 'Matières premières', accent: '#E0C281', bg: '#3a2e1a', desc: "Or, pétrole, métaux — l'économie réelle." },
  devises:     { label: 'Devises',            accent: '#7AE0C5', bg: '#1a3a32', desc: "Forex : échanger une monnaie pour une autre." },
  cryptos:     { label: 'Cryptos',            accent: '#E07AAB', bg: '#3a1a2c', desc: "Hyper volatil, hautement spéculatif." },
};

const normalizeType = (type) => {
  if (!type) return 'autres';
  const t = type.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  if (t.includes('action')) return 'actions';
  if (t.includes('oblig')) return 'obligations';
  if (t.includes('mati') || t.includes('prem')) return 'matieres';
  if (t.includes('devise') || t.includes('forex') || t.includes('change')) return 'devises';
  if (t.includes('crypto') || t.includes('bitcoin')) return 'cryptos';
  return t;
};

function Sparkline({ data, up }) {
  if (!data || data.length < 2) return <div style={{ width: 56, height: 22, flexShrink: 0 }}/>;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const W = 56, H = 22;
  const stepX = W / (data.length - 1);
  const pts = data.map((v, i) => [i * stepX, H - ((v - min) / range) * H]);
  const path = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const area = `${path} L${W},${H} L0,${H} Z`;
  const color = up ? '#4ADE9E' : '#FF7A78';
  const gradId = `sp${data.length}${Math.round(data[0])}`;
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', flexShrink: 0 }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3"/>
          <stop offset="100%" stopColor={color} stopOpacity="0"/>
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradId})`}/>
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round"/>
    </svg>
  );
}

function AssetMark({ marche, catKey }) {
  const meta = CATEGORY_META[catKey] || { accent: '#97A4BE', bg: '#1a2540' };
  return (
    <div style={{
      width: 40, height: 40, borderRadius: 12, flexShrink: 0,
      background: `linear-gradient(180deg, ${meta.bg} 0%, #0d1830 100%)`,
      border: '1px solid var(--border)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
    }}>
      {marche.icon ? (
        <img src={marche.icon} alt={marche.titre} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 11 }} />
      ) : (
        <span style={{ color: meta.accent, fontWeight: 700, fontSize: 12, letterSpacing: '0.02em' }}>
          {marche.titre?.slice(0, 2).toUpperCase()}
        </span>
      )}
    </div>
  );
}

const PortefeuilleMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const userId = useSelector(state => state.auth.user.id);
  const favoriteMarkets = useSelector(state => state.market.favoriteMarkets);
  const sparklinesCache = useSelector(state => state.market.sparklines);
  const pricesCache = useSelector(state => state.market.prices);
  const marchesCache = useSelector(state => state.market.marches);

  const [marches, setMarches] = useState(marchesCache);
  const [loading, setLoading] = useState(marchesCache.length === 0);
  const [tab, setTab] = useState(() => marchesCache.length > 0 ? normalizeType(marchesCache[0].type) : null);
  const [showFavorites, setShowFavorites] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [prices, setPrices] = useState(pricesCache);
  const [tradeMarche, setTradeMarche] = useState(null);
  const [country, setCountry] = useState(null);
  const SPARKLINE_TTL = 60_000;
  const [liquidite, setLiquidite] = useState(null);
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 1024);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Unique categories preserving API order
  const categories = useMemo(() => {
    const seen = new Set();
    return marches.reduce((acc, m) => {
      const key = normalizeType(m.type);
      if (!seen.has(key)) { seen.add(key); acc.push({ key, label: m.type }); }
      return acc;
    }, []);
  }, [marches]);

  // Markets for the active tab (or favorites)
  const tabMarches = useMemo(() => {
    if (showFavorites) return marches.filter(m => favoriteMarkets.some(f => f.marche_id === m.marche_id));
    if (!tab) return [];
    return marches.filter(m => normalizeType(m.type) === tab);
  }, [marches, tab, showFavorites, favoriteMarkets]);

  // Pays disponibles pour le tab courant (ordre alphabétique, null en tête = "Tous")
  const countries = useMemo(() => {
    const base = showFavorites || debouncedQuery ? [] : tabMarches;
    const seen = new Set();
    base.forEach(m => { if (m.pays) seen.add(m.pays); });
    return [...seen].sort((a, b) => a.localeCompare(b, 'fr'));
  }, [tabMarches, showFavorites, debouncedQuery]);

  // Filtered by search (searches all markets when query is active)
  const filtered = useMemo(() => {
    const base = debouncedQuery ? marches : tabMarches;
    let result = debouncedQuery
      ? base.filter(m => m.titre.toLowerCase().includes(debouncedQuery.toLowerCase()))
      : base;
    if (!debouncedQuery && !showFavorites && country) {
      result = result.filter(m => m.pays === country);
    }
    return [...result].sort((a, b) => a.titre.localeCompare(b.titre, 'fr'));
  }, [marches, tabMarches, debouncedQuery, showFavorites, country]);

  // Fetch all markets on mount (skipped if already in Redux cache)
  useEffect(() => {
    if (!userId) return;
    if (marchesCache.length > 0) {
      if (!tab) setTab(normalizeType(marchesCache[0].type));
      return;
    }
    (async () => {
      try {
        const data = await getMarches(userId);
        setMarches(data);
        dispatch(setMarchesCache(data));
        if (data.length > 0) setTab(normalizeType(data[0].type));
      } catch (error) {
        setMarches([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  // ── Pagination ──────────────────────────────────────────────
  const [visibleCount, setVisibleCount] = useState(10);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const sentinelRef = useRef(null);

  // Reset au changement de tab / filtre / recherche / pays
  useEffect(() => { setVisibleCount(10); setIsLoadingMore(false); }, [tab, showFavorites, debouncedQuery, country]);

  const visibleFiltered = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);

  // Sentinelle : ne s'active que quand le batch précédent est terminé
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || isLoadingMore) return; // bloqué pendant le chargement → pas de cascade
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisibleCount(c => c + 10);
    }, { rootMargin: '100px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [visibleFiltered, isLoadingMore]);

  // Fetch prix + sparklines en 2 requêtes batch pour les items visibles non cachés
  useEffect(() => {
    if (visibleFiltered.length === 0) return;
    const now = Date.now();
    const toFetch = visibleFiltered.filter(m => {
      const p = pricesCache[m.marche_id];
      const s = sparklinesCache[m.marche_id];
      return !p || now - p.at > SPARKLINE_TTL || !s || now - s.at > SPARKLINE_TTL;
    });
    if (!toFetch.length) return;

    setIsLoadingMore(true);
    let cancelled = false;
    (async () => {
      const items = toFetch.map(m => ({ marche_id: m.marche_id, league_id: m.league_id }));
      const [sparkData, priceData] = await Promise.all([
        getSparklineBatch(items),
        getPricesBatch(items),
      ]);
      if (cancelled) return;
      if (Object.keys(sparkData).length) dispatch(setSparklinesBatch(sparkData));
      const priceMap = {};
      Object.entries(priceData).forEach(([id, d]) => { if (d) priceMap[id] = d; });
      if (Object.keys(priceMap).length) {
        setPrices(prev => ({ ...prev, ...priceMap }));
        dispatch(setPricesBatch(priceMap));
      }
      setIsLoadingMore(false);
    })();
    return () => { cancelled = true; setIsLoadingMore(false); };
  }, [visibleFiltered]);

  useEffect(() => {
    if (!userId) return;
    getPortefeuilleData(userId)
      .then(data => setLiquidite(parseFloat(data.liquidite) || null))
      .catch(() => {});
  }, [userId]);

  // "Revoir un tuto" (page Profil) : la phase fiche_actif_intro n'a pas de page dédiée, elle se
  // déclenche à l'apparition de la fiche d'un actif. On rouvre donc la fiche du premier marché
  // disponible pour laisser TutoRunner détecter #trade-modal-root et rejouer le tuto.
  useEffect(() => {
    if (location.state?.reopenTutoPhase !== 'fiche_actif_intro') return;
    if (marches.length === 0 || tradeMarche) return;
    setTradeMarche(marches[0]);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, marches, tradeMarche, navigate, location.pathname]);

  const currentMeta = CATEGORY_META[tab] || null;

  if (loading) {
    return (
      <div className="marches-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ color: 'var(--text-mute)', fontSize: 13 }}>Chargement…</div>
      </div>
    );
  }

  return (
    <div className="marches-page">
    <div className="marches-content">

      {/* ── Header ── */}
      <div style={{ padding: '16px 0 14px' }} className="xl:mt-4">
        <div style={{ color: 'var(--text-mute)', fontSize: 13, fontWeight: 500 }}>Marchés</div>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
          <h1 style={{ margin: '2px 0 0', fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em' }}>Explorer</h1>
        </div>
      </div>

      {/* ── Search ── */}
      <div id="marches-search-bar" style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid var(--border)',
        borderRadius: 12, padding: '10px 12px',
      }}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="7" cy="7" r="4.5" stroke="var(--text-mute)" strokeWidth="1.5"/>
          <path d="M10.5 10.5L13.5 13.5" stroke="var(--text-mute)" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        <input
          value={query}
          onChange={e => { setQuery(e.target.value); setTab(e.target.value.length === 0 ? (categories[0]?.key ?? null) : null); }}
          placeholder="Rechercher un actif…"
          style={{
            flex: 1, background: 'none', border: 'none', outline: 'none',
            color: 'var(--text)', fontSize: 14, fontFamily: 'inherit',
          }}
        />
        {query && (
          <button onClick={() => { setQuery(''); setTab(categories[0]?.key ?? null); }} style={{
            background: 'none', border: 'none', color: 'var(--text-mute)',
            cursor: 'pointer', padding: 0, lineHeight: 1, display: 'flex',
          }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
        )}
      </div>

      {/* ── Category pills ── */}
      <div id="marches-category-pills" className="marches-pills">
        <button
          id="marches-favoris-btn"
          className="pill-tap"
          onClick={() => setShowFavorites(f => !f)}
          style={{
            padding: '6px 12px', borderRadius: 999,
            background: showFavorites ? 'rgba(224,194,129,0.18)' : 'rgba(255,255,255,0.04)',
            color: showFavorites ? 'var(--gold)' : 'var(--text-dim)',
            border: `1px solid ${showFavorites ? 'rgba(224,194,129,0.3)' : 'var(--border)'}`,
            fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12">
            <path d="M6 1.5L7.4 4.6 10.7 5 8.2 7.3 8.9 10.5 6 8.9 3.1 10.5 3.8 7.3 1.3 5 4.6 4.6z"
              fill={showFavorites ? 'var(--gold)' : 'currentColor'}/>
          </svg>
          Favoris
        </button>
        {categories.map(cat => (
          <button
            key={cat.key}
            className="pill-tap"
            onClick={() => { setTab(cat.key); setShowFavorites(false); setQuery(''); setCountry(null); }}
            style={{
              padding: '6px 12px', borderRadius: 999,
              background: !showFavorites && tab === cat.key ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.04)',
              color: !showFavorites && tab === cat.key ? '#0A1226' : 'var(--text-dim)',
              border: `1px solid ${!showFavorites && tab === cat.key ? 'transparent' : 'var(--border)'}`,
              fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* ── Category hero ── */}
      {!showFavorites && !debouncedQuery && currentMeta && (
        <div className="relief marches-hero" style={{ position: 'relative', overflow: 'hidden' }}>
          <div style={{
            position: 'absolute', top: -40, right: -40, width: 160, height: 160,
            background: `radial-gradient(closest-side, ${currentMeta.accent}22, transparent)`,
            pointerEvents: 'none',
          }}/>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative' }}>
            <div style={{ flex: 1 }}>
              <h2 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em' }}>
                {currentMeta.label}
              </h2>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--text-dim)', lineHeight: 1.5, maxWidth: 220 }}>
                {currentMeta.desc}
              </p>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
              <div className="num" style={{ fontSize: 18, fontWeight: 600, color: currentMeta.accent, letterSpacing: '-0.02em' }}>
                {tabMarches.length}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-mute)', marginTop: 2 }}>
                actif{tabMarches.length > 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Section header ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '20px 0 10px', gap: 10,
      }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em', flexShrink: 0 }}>
          {showFavorites ? 'Vos favoris' : debouncedQuery ? `Résultats` : currentMeta?.label || 'Marchés'}
        </h2>
        {countries.length > 1 && (
          <div style={{ display: 'flex', gap: 4, overflowX: 'auto', flexShrink: 1 }}>
            <button
              className="pill-tap"
              onClick={() => setCountry(null)}
              style={{
                padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                background: country === null ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.04)',
                color: country === null ? '#0A1226' : 'var(--text-dim)',
                border: `1px solid ${country === null ? 'transparent' : 'var(--border)'}`,
              }}
            >Tous</button>
            {countries.map(p => (
              <button
                key={p}
                className="pill-tap"
                onClick={() => setCountry(c => c === p ? null : p)}
                style={{
                  padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                  background: country === p ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.04)',
                  color: country === p ? '#0A1226' : 'var(--text-dim)',
                  border: `1px solid ${country === p ? 'transparent' : 'var(--border)'}`,
                }}
              >{p}</button>
            ))}
          </div>
        )}
      </div>

      {/* ── Asset list ── */}
      <div className="relief" style={{ borderRadius: 18, padding: '0 14px' }}>
        {visibleFiltered.map((marche, i) => {
          const p = prices[marche.marche_id];
          const value = p && p.value != null ? parseFloat(p.value) : null;
          const lastValue = p && p.last_value != null ? parseFloat(p.last_value) : null;
          const pct = Number.isFinite(value) && Number.isFinite(lastValue) && lastValue !== 0
            ? ((value - lastValue) / lastValue) * 100
            : null;
          const up = pct !== null ? pct >= 0 : true;
          const catKey = normalizeType(marche.type);

          return (
            <button
              key={marche.marche_id}
              onClick={() => setTradeMarche(marche)}
              className="pill-tap marches-row"
              style={{ borderBottom: i < visibleFiltered.length - 1 ? '1px solid var(--border)' : 'none' }}
            >
              <AssetMark marche={marche} catKey={catKey} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 14, fontWeight: 600, color: 'var(--text)',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {marche.titre}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-mute)', fontWeight: 500, marginTop: 2 }}>
                  {marche.type}
                </div>
              </div>
              {sparklinesCache[marche.marche_id]?.data
                ? <Sparkline data={sparklinesCache[marche.marche_id].data} up={up} />
                : <div className="animate-pulse" style={{ width: 56, height: 22, borderRadius: 4, background: 'rgba(255,255,255,0.07)', flexShrink: 0 }} />
              }
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0, gap: 3 }}>
                {Number.isFinite(value) ? (
                  <>
                    <span className="num" style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                      {value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    {pct !== null && (
                      <span className="num" style={{ fontSize: 11, fontWeight: 600, color: up ? 'var(--up)' : 'var(--down)' }}>
                        {pct >= 0 ? '+' : ''}{pct.toFixed(2)} %
                      </span>
                    )}
                  </>
                ) : (
                  <div className="animate-pulse" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                    <div style={{ width: 52, height: 14, borderRadius: 4, background: 'rgba(255,255,255,0.07)' }} />
                    <div style={{ width: 36, height: 11, borderRadius: 4, background: 'rgba(255,255,255,0.04)' }} />
                  </div>
                )}
              </div>
            </button>
          );
        })}

        {/* Skeleton de pagination — visible pendant le chargement des items suivants */}
        {visibleCount < filtered.length && (
          <div ref={sentinelRef} className="animate-pulse" style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0',
          }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(255,255,255,0.07)', flexShrink: 0 }} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ height: 13, width: '52%', borderRadius: 6, background: 'rgba(255,255,255,0.07)' }} />
              <div style={{ height: 10, width: '32%', borderRadius: 6, background: 'rgba(255,255,255,0.04)' }} />
            </div>
            <div style={{ width: 56, height: 22, borderRadius: 6, background: 'rgba(255,255,255,0.07)', flexShrink: 0 }} />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5, flexShrink: 0 }}>
              <div style={{ height: 13, width: 50, borderRadius: 6, background: 'rgba(255,255,255,0.07)' }} />
              <div style={{ height: 10, width: 34, borderRadius: 6, background: 'rgba(255,255,255,0.04)' }} />
            </div>
          </div>
        )}

        {filtered.length === 0 && (
          <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-mute)', fontSize: 13 }}>
            {query ? `Aucun résultat pour "${query}".` : showFavorites ? 'Aucun favori.' : 'Aucun marché disponible.'}
          </div>
        )}
      </div>

      {/* Modal plein écran sur mobile uniquement */}
      {!isDesktop && tradeMarche && (
        <TradeModal
          marche={tradeMarche}
          prices={prices}
          sparkline={sparklinesCache[tradeMarche.marche_id]?.data || []}
          liquidite={liquidite}
          onLiquiditeUpdate={setLiquidite}
          userId={userId}
          navigate={navigate}
          onClose={() => setTradeMarche(null)}
        />
      )}
    </div>

    {/* Panel latéral sticky sur desktop */}
    {isDesktop && (
      <div className="marches-side">
        {tradeMarche ? (
          <TradeModal
            marche={tradeMarche}
            prices={prices}
            sparkline={sparklinesCache[tradeMarche.marche_id]?.data || []}
            liquidite={liquidite}
            onLiquiditeUpdate={setLiquidite}
            userId={userId}
            navigate={navigate}
            onClose={() => setTradeMarche(null)}
            isPanel
          />
        ) : null}
      </div>
    )}
    </div>
  );
};

function TradeModal({ marche, prices, sparkline, liquidite, onLiquiditeUpdate, userId, navigate, onClose, isPanel = false }) {
  const [side, setSide] = useState('buy');
  const [amount, setAmount] = useState('500');
  const amountNum = parseFloat(amount) || 0;
  const [leverage, setLeverage] = useState(1);
  const [tradeDetail, setTradeDetail] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    getDataForCurrentMinutes(marche.marche_id, marche.league_id)
      .then(data => setTradeDetail(data))
      .catch(() => {});
  }, [marche.marche_id, marche.league_id]);

  useEffect(() => {
    if (isPanel) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [isPanel]);

  const p = prices[marche.marche_id];
  const currentVal = tradeDetail?.value != null ? parseFloat(tradeDetail.value)
    : (p?.value != null ? parseFloat(p.value) : null);
  const lastVal = tradeDetail?.last_value != null ? parseFloat(tradeDetail.last_value)
    : (p?.last_value != null ? parseFloat(p.last_value) : null);
  const diff = tradeDetail?.difference != null ? parseFloat(tradeDetail.difference)
    : (currentVal != null && lastVal != null && lastVal !== 0 ? ((currentVal - lastVal) / lastVal) * 100 : 0);
  const heure_complete = tradeDetail?.hour || '';
  const isUp = diff >= 0;

  const chartDiff = chartData
    ? ((chartData.lastClose - chartData.firstClose) / chartData.firstClose) * 100
    : diff;
  const chartIsUp = chartDiff >= 0;

  const handleSubmit = async () => {
    if (currentVal == null) return;
    setSubmitting(true);
    try {
      const price = parseFloat(currentVal).toFixed(2);
      const { marche_id, titre, icon, type, league_id } = marche;
      const response = side === 'buy'
        ? await funcAchat({
          user_id: userId,
          market_id: marche_id,
          league_id,
          montant: amountNum,
          price: parseFloat(price),
          leverage,
          type,
        })
        : await funcVente({
          user_id: userId,
          market_id: marche_id,
          league_id,
          montant: amountNum,
          price: parseFloat(price),
          leverage,
        });
      // La liquidité affichée est locale à cette page et ne se rafraîchit pas toute seule :
      // sans ça, après une vente le montant restant reste faux et les achats suivants sont refusés à tort.
      const newLiquidite = parseFloat(response?.portefeuille?.montant_actuel);
      if (!Number.isNaN(newLiquidite)) {
        onLiquiditeUpdate(newLiquidite);
      }
      setConfirmed({ side, titre, icon, price: parseFloat(price) });
    } catch (err) {
      toast.warning(err.message || 'Une erreur est survenue lors de la prise de position.');
    } finally {
      setSubmitting(false);
    }
  };

  const fmtVal = (v) => v != null ? v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

  const outerStyle = isPanel ? {
    position: 'relative',
    borderRadius: 20,
    border: '1px solid var(--border)',
    background: 'linear-gradient(180deg, #14213D 0%, #0E1830 100%)',
    overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
    maxHeight: 'calc(100vh - 72px)',
  } : {
    position: 'fixed', inset: 0, zIndex: 200,
    background: 'linear-gradient(180deg, #14213D 0%, #0E1830 100%)',
    display: 'flex', flexDirection: 'column',
  };

  return (
    <div
      id="trade-modal-root"
      onClick={e => e.stopPropagation()}
      style={outerStyle}
    >

        {/* Header — toujours visible en haut, hors du scroll */}
        <div style={{
          flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '16px 16px 12px',
          background: 'rgba(20,33,61,0.1)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderBottom: '1px solid var(--border)',
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 12, flexShrink: 0,
            overflow: 'hidden', background: 'rgba(255,255,255,0.06)',
            border: '1px solid var(--border)', display: 'grid', placeItems: 'center',
          }}>
            {marche.icon
              ? <img src={marche.icon} alt={marche.titre} style={{ width: '100%', height: '100%', objectFit: 'cover' }}/>
              : <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-dim)' }}>{marche.titre?.slice(0, 2).toUpperCase()}</span>
            }
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em' }}>{marche.titre}</div>
            <div style={{ fontSize: 11, color: 'var(--text-mute)', marginTop: 1 }}>{marche.type}</div>
          </div>
          <button onClick={onClose} style={{
            width: 32, height: 32, borderRadius: 16,
            border: '1px solid var(--border)',
            background: 'rgba(255,255,255,0.04)', color: 'var(--text-dim)',
            display: 'grid', placeItems: 'center', cursor: 'pointer',
          }}>
            <svg width="12" height="12" viewBox="0 0 12 12"><path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
          </button>
        </div>

        {/* Contenu scrollable */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 16px', paddingBottom: isPanel ? 24 : 40 }}>

        {/* Price */}
        <div id="trade-quote" style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 14 }}>
          <span className="num" style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.02em' }}>
            {fmtVal(currentVal)}
            <span style={{ color: 'var(--text-dim)', fontWeight: 500, fontSize: 20 }}> €</span>
          </span>
          <span className="num" style={{
            color: chartIsUp ? 'var(--up)' : 'var(--down)',
            background: chartIsUp ? 'var(--up-soft)' : 'var(--down-soft)',
            padding: '3px 8px', borderRadius: 8, fontSize: 12, fontWeight: 600,
          }}>{chartIsUp ? '+' : ''}{chartDiff.toFixed(2)} %</span>
        </div>

        {/* Chart */}
        <div id="trade-chart" style={{ marginTop: 10, borderRadius: 14, overflow: 'hidden' }}>
          <StockChart marcheId={String(marche.marche_id)} leagueid={String(marche.league_id)} onDataLoad={setChartData} />
        </div>

        {/* Stats */}
        {(() => {
          const spHigh = chartData ? chartData.high : (sparkline.length > 0 ? Math.max(...sparkline) : null);
          const spLow  = chartData ? chartData.low  : (sparkline.length > 0 ? Math.min(...sparkline) : null);
          const stats = [
            { label: 'Plus haut', value: spHigh != null ? fmtVal(spHigh) + ' €' : '—' },
            { label: 'Plus bas',  value: spLow  != null ? fmtVal(spLow)  + ' €' : '—' },
            { label: 'Variation', value: (chartIsUp ? '+' : '') + chartDiff.toFixed(2) + ' %', color: chartIsUp ? 'var(--up)' : 'var(--down)' },
          ];
          return (
            <div id="trade-stats" style={{
              marginTop: 14, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
              gap: 1, background: 'rgba(255,255,255,0.06)',
              border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden',
            }}>
              {stats.map(s => (
                <div key={s.label} style={{ background: 'rgba(255,255,255,0.04)', padding: '10px 12px' }}>
                  <div style={{ fontSize: 10, color: 'var(--text-mute)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{s.label}</div>
                  <div className="num" style={{ fontSize: 13, fontWeight: 600, color: s.color || 'var(--text)', marginTop: 3 }}>{s.value}</div>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Buy / Sell tabs */}
        <div id="trade-buy-sell-tabs" style={{
          marginTop: 18, display: 'flex', gap: 4,
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid var(--border)',
          borderRadius: 12, padding: 4,
        }}>
          {[
            { id: 'buy',  label: 'ACHETER · LONG',  activeBg: 'var(--up-soft)',   activeColor: 'var(--up)',   activeBorder: 'rgba(74,222,158,0.3)'  },
            { id: 'sell', label: 'VENDRE · SHORT', activeBg: 'var(--down-soft)', activeColor: 'var(--down)', activeBorder: 'rgba(255,122,120,0.3)' },
          ].map(t => (
            <button key={t.id} onClick={() => setSide(t.id)} style={{
              flex: 1, padding: '10px 0', borderRadius: 9, border: 'none', cursor: 'pointer',
              background: side === t.id ? t.activeBg : 'transparent',
              color: side === t.id ? t.activeColor : 'var(--text-dim)',
              fontSize: 13, fontWeight: 700, letterSpacing: '0.02em',
              boxShadow: side === t.id ? `inset 0 0 0 1px ${t.activeBorder}` : 'none',
            }}>{t.label}</button>
          ))}
        </div>

        {/* Amount */}
        <div id="trade-amount-card" style={{
          marginTop: 12, background: 'rgba(255,255,255,0.04)',
          border: '1px solid var(--border)', borderRadius: 16, padding: 14,
        }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 11, color: 'var(--text-mute)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Montant</span>
            {liquidite != null && (
              <span style={{ fontSize: 11, color: 'var(--text-mute)' }}>
                Liquidités <span className="num ms-1" style={{ color: 'var(--text-dim)', fontWeight: 600 }}>
                  {liquidite.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                </span>
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 6 }}>
            <input
              className="num"
              type="number"
              inputMode="decimal"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              style={{
                flex: 1, background: 'none', border: 'none', outline: 'none',
                color: 'var(--text)', fontSize: 28, fontWeight: 600, letterSpacing: '-0.02em',
                fontFamily: 'Geist Mono, Roboto Mono, monospace', width: '100%', padding: 0,
              }}
            />
            <span style={{ color: 'var(--text-dim)', fontSize: 18, fontWeight: 600 }}>€</span>
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
            {[100, 500, 1000, 2500].map(v => (
              <button key={v} onClick={() => setAmount(String(v))} style={{
                flex: 1, padding: '7px 0', borderRadius: 8,
                background: amountNum === v ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.04)',
                border: '1px solid var(--border)',
                color: 'var(--text-dim)', fontSize: 11, fontWeight: 600, cursor: 'pointer',
              }}><span className="num">{v} €</span></button>
            ))}
          </div>
        </div>

        {/* Leverage */}
        <div id="trade-leverage-card" style={{
          marginTop: 10, background: 'rgba(255,255,255,0.04)',
          border: '1px solid var(--border)', borderRadius: 16, padding: 14,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-mute)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Levier</div>
              <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>
                Exposition <span className="num" style={{ color: 'var(--text)', fontWeight: 600 }}>{(amountNum * leverage).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €</span>
              </div>
            </div>
            <div className="num" style={{
              fontSize: 20, fontWeight: 700,
              color: leverage === 1 ? 'var(--text)' : 'var(--gold)',
              letterSpacing: '-0.02em',
            }}>×{leverage}</div>
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
            {[1, 2, 3, 5, 10].map(l => (
              <button key={l} onClick={() => setLeverage(l)} style={{
                flex: 1, padding: '8px 0', borderRadius: 8,
                background: leverage === l ? 'rgba(224,194,129,0.14)' : 'rgba(255,255,255,0.04)',
                border: '1px solid ' + (leverage === l ? 'rgba(224,194,129,0.3)' : 'var(--border)'),
                color: leverage === l ? 'var(--gold)' : 'var(--text-dim)',
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
              }}>×{l}</button>
            ))}
          </div>
          {leverage > 1 && (
            <p style={{
              margin: '10px 0 0', padding: '8px 10px',
              borderRadius: 8, background: 'var(--gold-soft)',
              border: '1px solid rgba(224,194,129,0.2)',
              fontSize: 11, color: 'var(--gold)', lineHeight: 1.5,
            }}>
              Le levier amplifie gains <em>et</em> pertes — utilise avec prudence.
            </p>
          )}
        </div>

        {/* Summary */}
        {currentVal != null && (
          <div style={{
            marginTop: 10, background: 'rgba(255,255,255,0.025)',
            border: '1px solid var(--border)', borderRadius: 14, padding: '12px 14px',
            display: 'flex', flexDirection: 'column', gap: 8,
          }}>
            {[
              { label: "Prix d'exécution", value: fmtVal(currentVal) + ' €' },
              { label: 'Total',            value: (amountNum).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €', bold: true },
            ].map(r => (
              <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: 12, color: 'var(--text-mute)' }}>{r.label}</span>
                <span className="num" style={{ fontSize: r.bold ? 14 : 12, fontWeight: r.bold ? 700 : 500, color: 'var(--text)' }}>{r.value}</span>
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <button
          onClick={handleSubmit}
          disabled={submitting || currentVal == null}
          style={{
            marginTop: 14, width: '100%', height: 56, borderRadius: 16,
            background: side === 'buy'
              ? 'linear-gradient(180deg, #56e8a6 0%, #2bbf80 100%)'
              : 'linear-gradient(180deg, #ff8e8c 0%, #e25956 100%)',
            color: '#0a1226', border: 'none',
            cursor: submitting ? 'wait' : 'pointer',
            fontWeight: 700, fontSize: 15, letterSpacing: '0.01em',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4), 0 8px 24px rgba(0,0,0,0.4)',
            opacity: (submitting || currentVal == null) ? 0.65 : 1,
          }}
        >
          {submitting
            ? 'En cours…'
            : `${side === 'buy' ? 'Acheter' : 'Vendre'} ${marche.titre} · ${amountNum.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`}
        </button>
        <div style={{ textAlign: 'center', fontSize: 10, color: 'var(--text-mute)', marginTop: 8 }}>
          Argent virtuel · simulation pédagogique
        </div>
        </div>{/* fin contenu scrollable */}

      {/* Écran de confirmation animé */}
      {confirmed && (
        <>
          <style>{`
            @keyframes tcBg {
              from { opacity: 0 }
              to   { opacity: 1 }
            }
            @keyframes tcText {
              from { opacity: 0; transform: translateY(18px) }
              to   { opacity: 1; transform: translateY(0) }
            }
            @keyframes tcCheck {
              0%   { opacity: 0; transform: scale(0.15) }
              65%  { transform: scale(1.12) }
              82%  { transform: scale(0.94) }
              100% { opacity: 1; transform: scale(1) }
            }
            @keyframes tcBtn {
              from { opacity: 0; transform: translateY(10px) }
              to   { opacity: 1; transform: translateY(0) }
            }
          `}</style>
          <div style={{
            position: 'absolute', inset: 0, zIndex: 30,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(180deg, #14213D 0%, #0E1830 100%)',
            borderRadius: isPanel ? 20 : 0,
            padding: '32px 24px',
            animation: 'tcBg 0.35s ease forwards',
          }}>

            {/* Libellé + détails */}
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
              marginBottom: 40,
              animation: 'tcText 0.4s ease 0.4s both',
            }}>
              <span style={{
                fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
                textTransform: 'uppercase', color: 'var(--text-mute)',
              }}>
                {confirmed.side === 'buy' ? 'Position longue ouverte' : 'Position courte ouverte'}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                {confirmed.icon && (
                  <img src={confirmed.icon} alt={confirmed.titre}
                    style={{ width: 36, height: 36, borderRadius: 10, objectFit: 'cover' }} />
                )}
                <span style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em' }}>{confirmed.titre}</span>
              </div>
              <span className="num" style={{ fontSize: 15, color: 'var(--text-dim)', marginTop: 2 }}>
                {confirmed.price.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
              </span>
            </div>

            {/* Grosse icône check */}
            <div style={{
              width: 100, height: 100, borderRadius: 50,
              background: confirmed.side === 'buy' ? 'rgba(86,232,166,0.10)' : 'rgba(255,142,140,0.10)',
              border: `2px solid ${confirmed.side === 'buy' ? '#56e8a6' : '#ff8e8c'}`,
              display: 'grid', placeItems: 'center',
              marginBottom: 44,
              boxShadow: confirmed.side === 'buy'
                ? '0 0 40px rgba(86,232,166,0.20)'
                : '0 0 40px rgba(255,142,140,0.20)',
              animation: 'tcCheck 0.65s cubic-bezier(.34,1.56,.64,1) 0.85s both',
            }}>
              <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
                <path
                  d="M13 26L22 36L39 16"
                  stroke={confirmed.side === 'buy' ? '#56e8a6' : '#ff8e8c'}
                  strokeWidth="3.8" strokeLinecap="round" strokeLinejoin="round"
                />
              </svg>
            </div>

            {/* Bouton fermer */}
            <button
              onClick={onClose}
              style={{
                padding: '15px 48px', borderRadius: 16,
                background: confirmed.side === 'buy'
                  ? 'linear-gradient(180deg, #56e8a6 0%, #2bbf80 100%)'
                  : 'linear-gradient(180deg, #ff8e8c 0%, #e25956 100%)',
                color: '#0a1226', border: 'none', cursor: 'pointer',
                fontWeight: 700, fontSize: 15, letterSpacing: '0.01em',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4), 0 8px 24px rgba(0,0,0,0.4)',
                animation: 'tcBtn 0.35s ease 1.45s both',
              }}
            >
              Fermer
            </button>
          </div>
        </>
      )}

    </div>
  );
}

export default PortefeuilleMenu;
